import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useUser } from "../../Context/UserContext";
import { DataGrid, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Typography,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";

const ProductioncentersaplingReport = () => {
  const { id } = useParams(); // block_id
  const navigate = useNavigate();
  const { token } = useUser();
  const apiRef = useGridApiRef();

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchText, setSearchText] = useState("");
  const [selectedSapling, setSelectedSapling] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  // Dropdown Options
  const [saplingOptions, setSaplingOptions] = useState([]);
  const [centerOptions, setCenterOptions] = useState([]);

  useEffect(() => {
    if (!token || !id) return;

    setLoading(true);
    axios
      .get(`/api/center/productioncenter-sapling-summary/?block_id=${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data && res.data.saplings) {
          const data = res.data.saplings;
          setReportData(data);

          // 1. Generate Sapling Options
          const uniqueSaplings = Array.from(new Set(data.map((s) => s.sapling_name)))
            .filter(Boolean)
            .sort()
            .map((name) => ({ label: name }));
          setSaplingOptions(uniqueSaplings);

          // 2. Generate Center Options (Flattening the center_names arrays)
          const centersMap = new Map();
          data.forEach((row) => {
            if (Array.isArray(row.center_names)) {
              row.center_names.forEach((c) => {
                if (c.id && c.name) centersMap.set(String(c.id), c.name);
              });
            }
          });
          
          const formattedCenters = Array.from(centersMap.entries()).map(([cid, cname]) => ({
            id: cid,
            label: cname,
          }));
          setCenterOptions(formattedCenters);
        }
      })
      .catch((err) => console.error("Error fetching data:", err))
      .finally(() => setLoading(false));
  }, [token, id]);

  // Combined Filtering Logic
  const filteredData = useMemo(() => {
    return reportData.filter((row) => {
      // Search Box Filter
      const matchesSearch = !searchText || 
        row.sapling_name?.toLowerCase().includes(searchText.toLowerCase());
      
      // Sapling Autocomplete Filter
      const matchesSapling = !selectedSapling || 
        row.sapling_name === selectedSapling.label;

      // Center Autocomplete Filter
      const matchesCenter = !selectedCenter || 
        (Array.isArray(row.center_names) && row.center_names.some(c => String(c.id) === String(selectedCenter.id)));

      return matchesSearch && matchesSapling && matchesCenter;
    });
  }, [reportData, searchText, selectedSapling, selectedCenter]);

  const columns = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        width: 70,
        renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      },
      {
        field: "sapling_name",
        headerName: "Sapling Name",
        minWidth: 180,
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "center_names",
        headerName: "Production Centers",
        minWidth: 280,
        flex: 2,
        renderCell: (params) => {
          const centers = params.row.center_names || [];
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {centers.slice(0, 2).map((center) => (
                <Chip
                  key={center.id}
                  label={center.name}
                  size="small"
                  sx={{ bgcolor: "#f3e5f5", color: "#7b1fa2", fontSize: "0.7rem" }}
                />
              ))}
              {centers.length > 2 && (
                <Tooltip title={centers.slice(2).map((c) => c.name).join(", ")}>
                  <Chip label={`+${centers.length - 2}`} size="small" />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      { field: "total_stock", headerName: "Stock", type: "number", width: 100 },
      { field: "total_sold", headerName: "Sold", type: "number", width: 100 },
      {
        field: "total_sale_price",
        headerName: "Value (₹)",
        width: 130,
        renderCell: (p) => `₹${Number(p.value || 0).toLocaleString("en-IN")}`,
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 60px)", display: "flex", flexDirection: "column", bgcolor: "#f4f6f8" }}>
      {/* FILTER BAR */}
      <Box 
        sx={{ 
          display: "flex", 
          gap: 2, 
          mb: 2, 
          p: 2, 
          bgcolor: "white", 
          borderRadius: 2, 
          alignItems: "center",
          flexWrap: "wrap",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)" 
        }}
      >
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)} 
          size="small"
        >
          Back
        </Button>

        <TextField
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 180 }}
        />

        <Autocomplete
          size="small"
          options={saplingOptions}
          value={selectedSapling}
          onChange={(e, val) => setSelectedSapling(val)}
          renderInput={(params) => <TextField {...params} label="Select Sapling " />}
          sx={{ width: 180 }}
        />

        <Autocomplete
          size="small"
          options={centerOptions}
          value={selectedCenter}
          onChange={(e, val) => setSelectedCenter(val)}
          renderInput={(params) => <TextField {...params} label="Select Center" />}
          sx={{ width: 180 }}
        />

        <Button 
          onClick={() => { setSelectedSapling(null); setSelectedCenter(null); setSearchText(""); }}
          sx={{ textTransform: "none", color: "gray" }}
        >
          Reset
        </Button>

        <Box sx={{ flexGrow: 1 }} />

        <Button 
          variant="contained" 
          onClick={() => apiRef.current.exportDataAsCsv()} 
          sx={{ bgcolor: "#1565c0" }}
        >
          Download CSV
        </Button>
      </Box>

      {/* DATA GRID */}
      <Box sx={{ flexGrow: 1, bgcolor: "white", borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          apiRef={apiRef}
          rows={filteredData}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.species_id || Math.random()}
          slots={{ toolbar: GridToolbar }}
          disableRowSelectionOnClick
        />
      </Box>
    </Box>
  );
};

export default ProductioncentersaplingReport;